import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../contexts/CartContext";

// Styles personnalisés optimisés
const styles = {
  heroSection: {
    backgroundColor: "#543ba4",
    borderRadius: "0",
    padding: "3rem 0",
    marginBottom: "2rem",
  },
  sectionTitle: {
    position: "relative",
    marginBottom: "2.5rem",
    textAlign: "center",
    fontWeight: "600",
  },
  sectionTitleLine: {
    content: '""',
    display: "block",
    width: "60px",
    height: "3px",
    backgroundColor: "#543ba4",
    margin: "1rem auto",
    borderRadius: "2px",
  },
  boxIcon: {
    fontSize: "2.5rem",
    color: "#543ba4",
  },
  processCircle: {
    width: "100px",
    height: "100px",
    backgroundColor: "white",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
    margin: "0 auto 1.5rem",
    border: "2px solid #f3f3f3",
  },
  processNumber: {
    position: "absolute",
    top: "-10px",
    right: "-10px",
    backgroundColor: "#f89638",
    color: "white",
    width: "35px",
    height: "35px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.1rem",
    fontWeight: "bold",
  },
  testimonialCard: {
    height: "100%",
    borderRadius: "0.75rem",
    overflow: "hidden",
    boxShadow: "0 0.25rem 0.75rem rgba(0, 0, 0, 0.08)",
    border: "none",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
  },
  testimonialHeader: {
    backgroundImage: "linear-gradient(45deg, #543ba4, #7e6bca)",
    padding: "1.5rem",
    color: "white",
  },
  testimonialAvatar: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    backgroundColor: "white",
    color: "#543ba4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.5rem",
    fontWeight: "bold",
    boxShadow: "0 0.25rem 0.5rem rgba(0, 0, 0, 0.1)",
  },
  categoryTag: {
    backgroundColor: "#f3f0ff",
    color: "#543ba4",
    fontWeight: "500",
    borderRadius: "50rem",
    padding: "0.35rem 0.75rem",
    fontSize: "0.85rem",
  },
  priceTag: {
    color: "#543ba4",
    fontWeight: "700",
    fontSize: "1.25rem",
  },
  btnHighlight: {
    backgroundColor: "#f89638",
    borderColor: "#f89638",
    color: "white",
    fontWeight: "500",
    padding: "0.5rem 1.5rem",
    borderRadius: "0.5rem",
    transition: "all 0.3s ease",
    border: "none",
    cursor: "pointer",
  },
  btnOutlineHighlight: {
    backgroundColor: "transparent",
    borderColor: "#f89638",
    color: "#f89638",
    fontWeight: "500",
    padding: "0.5rem 1.5rem",
    borderRadius: "0.5rem",
    transition: "all 0.3s ease",
    textDecoration: "none",
    display: "inline-block",
  },
  featureIcon: {
    width: "70px",
    height: "70px",
    backgroundColor: "#f3f0ff",
    color: "#543ba4",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.75rem",
    marginBottom: "1rem",
  },
  grayBg: {
    backgroundColor: "#f8f9fa",
  },
  purchaseOptionCard: {
    border: "1px solid #e9ecef",
    borderRadius: "0.75rem",
    padding: "1.5rem",
    height: "100%",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    cursor: "pointer",
    backgroundColor: "white",
    display: "flex",
    flexDirection: "column",
  },
  purchaseOptionCardHover: {
    transform: "translateY(-5px)",
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.1)",
  },
  purchaseOptionCardActive: {
    borderColor: "#543ba4",
    boxShadow: "0 0.25rem 0.75rem rgba(84, 59, 164, 0.15)",
  },
  productCard: {
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  productCardBody: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
  },
  productDescription: {
    flex: 1,
    minHeight: "60px", // Hauteur minimale pour uniformiser
  },
  processCardBody: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
};

// Images d'exemple de qualité
const HERO_IMAGE = "https://images.unsplash.com/photo-1549298916-b41d501d3772?w=600&h=400&fit=crop";

const Home = () => {
  const { products, addItem } = useCart();
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseOption, setPurchaseOption] = useState("single");

  // Produits en vedette mémorisés (limités à 4 pour la homepage)
  const featuredProducts = useMemo(() => {
    return products.slice(0, 4);
  }, [products]);

  // Simulation du chargement initial
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Gestionnaire d'ajout au panier optimisé avec useCallback
  const handleAddToCart = useCallback((product, isSubscription = false) => {
    addItem(product, isSubscription);
  }, [addItem]);

  // Gestionnaire de changement d'option d'achat
  const handlePurchaseOptionChange = useCallback((option) => {
    setPurchaseOption(option);
  }, []);

  // Effets hover pour les cartes produits
  const handleProductCardHover = useCallback((e, isEntering) => {
    if (isEntering) {
      e.currentTarget.style.transform = "translateY(-5px)";
      e.currentTarget.style.boxShadow = "0 0.5rem 1rem rgba(0,0,0,0.15)";
    } else {
      e.currentTarget.style.transform = "translateY(0)";
      e.currentTarget.style.boxShadow = "0 0.25rem 0.75rem rgba(0, 0, 0, 0.08)";
    }
  }, []);

  // Données des témoignages
  const testimonials = useMemo(() => [
    {
      id: 1,
      name: "Marie L.",
      avatar: "ML",
      duration: "Cliente depuis 6 mois",
      rating: 5,
      text: "J'adore recevoir ma box chaque mois. Les produits sont toujours de qualité et les surprises bien pensées. Je découvre des marques que je n'aurais jamais connues autrement."
    },
    {
      id: 2,
      name: "Pierre T.",
      avatar: "PT",
      duration: "Client depuis 1 an",
      rating: 4,
      text: "Le rapport qualité/prix est excellent. J'ai découvert plein de produits que je n'aurais jamais achetés autrement. Le service client est très réactif et à l'écoute."
    },
    {
      id: 3,
      name: "Sophie B.",
      avatar: "SB",
      duration: "Cliente depuis 3 mois",
      rating: 4.5,
      text: "J'ai offert cette box à ma sœur pour son anniversaire et elle en est ravie ! Le site est facile à utiliser et la personnalisation des box est un vrai plus. Je recommande vivement !"
    }
  ], []);

  // Données des étapes du processus
  const processSteps = useMemo(() => [
    {
      step: 1,
      icon: "bi-box-seam",
      title: "Choisissez votre box",
      description: "Sélectionnez parmi nos différentes box celle qui correspond le mieux à vos envies et vos besoins."
    },
    {
      step: 2,
      icon: "bi-cart-check",
      title: "Commandez en ligne",
      description: "Commandez facilement votre box en ligne, avec possibilité d'achat unique ou d'abonnement flexible selon vos préférences."
    },
    {
      step: 3,
      icon: "bi-gift",
      title: "Recevez et profitez",
      description: "Votre box est livrée directement chez vous. Déballez et découvrez les surprises sélectionnées pour vous !"
    }
  ], []);

  // Données des caractéristiques
  const features = useMemo(() => [
    {
      icon: "bi-star",
      title: "Produits de qualité",
      description: "Chaque produit est soigneusement sélectionné pour sa qualité et son originalité."
    },
    {
      icon: "bi-truck",
      title: "Livraison gratuite",
      description: "Profitez de la livraison gratuite en Belgique pour toute commande dès 50€."
    },
    {
      icon: "bi-arrow-repeat",
      title: "Flexible et sans engagement",
      description: "Achat unique ou abonnement, vous restez libre de choisir ce qui vous convient le mieux."
    },
    {
      icon: "bi-heart",
      title: "Satisfaction garantie",
      description: "Nous vous remboursons si vous n'êtes pas satisfait de votre box (sous 30 jours)."
    }
  ], []);

  // Options d'achat
  const purchaseOptions = useMemo(() => [
    {
      id: "single",
      icon: "bi-bag",
      title: "Achat unique",
      description: "Commandez une box sans engagement. Idéal pour essayer nos produits ou offrir un cadeau.",
      features: [
        "Livraison sous 24h-48h",
        "Sans engagement",
        "Parfait comme cadeau",
        "Possibilité de personnalisation"
      ],
      buttonText: "Découvrir nos box",
      buttonStyle: styles.btnHighlight
    },
    {
      id: "subscription",
      icon: "bi-calendar-check",
      title: "Abonnement flexible",
      description: "Recevez régulièrement votre box préférée à un tarif avantageux. Modifiable ou résiliable à tout moment.",
      hasBadge: true,
      features: [
        "Économisez jusqu'à 15% sur chaque box",
        "Fréquence au choix : mensuelle ou trimestrielle",
        "Modifiable ou résiliable à tout moment",
        "Accès prioritaire aux éditions limitées"
      ],
      buttonText: "Découvrir nos abonnements",
      buttonStyle: { ...styles.btnHighlight, backgroundColor: "transparent", borderColor: "#543ba4", color: "#543ba4" }
    }
  ], []);

  // Fonction pour afficher les étoiles
  const renderStars = useCallback((rating) => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div className="mb-2 text-warning">
        {Array(fullStars).fill().map((_, i) => (
          <i key={`full-${i}`} className="bi bi-star-fill"></i>
        ))}
        {hasHalfStar && <i className="bi bi-star-half"></i>}
        {Array(emptyStars).fill().map((_, i) => (
          <i key={`empty-${i}`} className="bi bi-star"></i>
        ))}
      </div>
    );
  }, []);

  return (
    <>
      {/* Hero Section avec Call-to-Action */}
      <section style={styles.heroSection} className="text-white">
        <div className="container">
          <div className="row align-items-center gy-4">
            <div className="col-lg-6 py-lg-5">
              <span className="badge bg-light text-dark mb-3 px-3 py-2">
                Livraison gratuite en Belgique dès 50€
              </span>
              <h1 className="display-4 fw-bold mb-3">
                Découvrez nos Box de produits exclusifs
              </h1>
              <p className="lead mb-4">
                Une sélection de produits de haute qualité livrés directement
                chez vous pour vous faire plaisir ou offrir.
              </p>
              <div className="d-flex flex-wrap gap-2">
                <Link to="/category" className="btn btn-light btn-lg px-4 py-2">
                  Voir nos box
                </Link>
                <Link
                  to="/how-it-works"
                  className="btn btn-outline-light btn-lg px-4 py-2"
                >
                  En savoir plus
                </Link>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="position-relative">
                <img
                  src={HERO_IMAGE}
                  alt="Box Présentation"
                  className="img-fluid rounded shadow-lg"
                  loading="lazy"
                />
                <div
                  className="position-absolute top-0 end-0 mt-4 me-4 bg-white rounded-pill shadow-sm px-3 py-2"
                  style={{ color: "#543ba4", fontWeight: "600" }}
                >
                  <i
                    className="bi bi-gift me-2"
                    style={{ color: "#f89638" }}
                  ></i>
                  Livré chez vous
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section des produits en vedette */}
      <section className="py-5">
        <div className="container">
          <h2 style={styles.sectionTitle}>
            Nos Box Populaires
            <span style={styles.sectionTitleLine}></span>
          </h2>

          {isLoading ? (
            // État de chargement
            <div className="text-center py-5">
              <div
                className="spinner-border"
                style={{ color: "#543ba4" }}
                role="status"
              >
                <span className="visually-hidden">Chargement...</span>
              </div>
              <p className="mt-3">Chargement de nos meilleures box...</p>
            </div>
          ) : (
            // Affichage des produits avec hauteur uniforme
            <div className="row g-4 align-items-stretch">
              {featuredProducts.map((product) => (
                <div key={product.id} className="col-md-6 col-lg-3 d-flex">
                  <div 
                    className="card border-0 shadow-sm w-100"
                    style={styles.productCard}
                    onMouseEnter={(e) => handleProductCardHover(e, true)}
                    onMouseLeave={(e) => handleProductCardHover(e, false)}
                  >
                    <Link 
                      to={`/product/${product.id}`}
                      className="text-decoration-none text-reset flex-grow-1 d-flex flex-column"
                    >
                      <div className="position-relative">
                        <img
                          src={product.image}
                          className="card-img-top"
                          alt={product.name}
                          style={{ height: "200px", objectFit: "cover" }}
                          loading="lazy"
                        />
                        <span
                          className="position-absolute top-0 end-0 m-2"
                          style={styles.categoryTag}
                        >
                          {product.category}
                        </span>
                        {product.isNew && (
                          <span
                            className="position-absolute top-0 start-0 m-2 badge"
                            style={{ backgroundColor: "#f89638" }}
                          >
                            Nouveau
                          </span>
                        )}
                      </div>
                      <div className="card-body" style={styles.productCardBody}>
                        <h5 className="card-title">{product.name}</h5>
                        <div style={styles.productDescription}>
                          <p className="card-text text-muted">
                            {product.description}
                          </p>
                        </div>
                        
                        {product.rating && (
                          <div className="mb-3">
                            <div className="d-flex align-items-center">
                              <div className="text-warning me-2">
                                {Array.from({ length: 5 }, (_, i) => (
                                  <i key={i} className={`bi ${i < product.rating ? 'bi-star-fill' : 'bi-star'} small`}></i>
                                ))}
                              </div>
                              <span className="text-muted small">
                                ({product.rating.toFixed(1)}) • {product.reviewCount} avis
                              </span>
                            </div>
                          </div>
                        )}
                        
                        <div className="mt-auto">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <span style={styles.priceTag}>
                              {product.price.toFixed(2)} €
                            </span>
                            {product.hasSubscriptionOption && (
                              <small className="text-success">
                                <i className="bi bi-arrow-repeat me-1"></i>
                                Abonnement
                              </small>
                            )}
                          </div>
                        </div>
                      </div>
                    </Link>
                    
                    <div className="card-body pt-0">
                      <button
                        className="btn w-100"
                        style={styles.btnHighlight}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleAddToCart(product);
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#e67e22";
                          e.target.style.transform = "translateY(-1px)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#f89638";
                          e.target.style.transform = "translateY(0)";
                        }}
                      >
                        <i className="bi bi-cart-plus me-1"></i> Ajouter au panier
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-5">
            <Link
              to="/category"
              className="btn btn-lg"
              style={styles.btnOutlineHighlight}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#f89638";
                e.target.style.color = "white";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#f89638";
              }}
            >
              Voir toutes nos box <i className="bi bi-arrow-right ms-1"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Comment ça marche */}
      <section style={styles.grayBg} className="py-5">
        <div className="container py-4">
          <h2 style={styles.sectionTitle}>
            Comment ça marche
            <span style={styles.sectionTitleLine}></span>
          </h2>

          <div className="row g-4 align-items-stretch">
            {processSteps.map((item) => (
              <div key={item.step} className="col-md-4 d-flex">
                <div className="card border-0 bg-transparent w-100">
                  <div className="card-body text-center" style={styles.processCardBody}>
                    <div
                      style={styles.processCircle}
                      className="position-relative"
                    >
                      <i className={`bi ${item.icon}`} style={styles.boxIcon}></i>
                      <div style={styles.processNumber}>{item.step}</div>
                    </div>
                    <h4 className="mb-3">{item.title}</h4>
                    <div className="mt-auto">
                      <p className="text-muted">{item.description}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <Link to="/how-it-works" className="btn btn-primary btn-lg">
              En savoir plus
            </Link>
          </div>
        </div>
      </section>

      {/* Options d'achat */}
      <section className="py-5">
        <div className="container py-4">
          <h2 style={styles.sectionTitle}>
            Choisissez l'option qui vous convient
            <span style={styles.sectionTitleLine}></span>
          </h2>

          <div className="row g-4 mb-5 align-items-stretch">
            {purchaseOptions.map((option) => (
              <div key={option.id} className="col-md-6 d-flex">
                <div
                  style={{
                    ...styles.purchaseOptionCard,
                    ...(purchaseOption === option.id
                      ? styles.purchaseOptionCardActive
                      : {}),
                    ...(purchaseOption === option.id
                      ? styles.purchaseOptionCardHover
                      : {}),
                  }}
                  className="text-center w-100"
                  onClick={() => handlePurchaseOptionChange(option.id)}
                >
                  <div className="mb-3">
                    {option.hasBadge ? (
                      <div className="position-relative">
                        <i
                          className={`bi ${option.icon}`}
                          style={{ fontSize: "2.5rem", color: "#543ba4" }}
                        ></i>
                        <span
                          className="position-absolute top-0 start-100 translate-middle badge rounded-pill"
                          style={{ backgroundColor: "#f89638" }}
                        >
                          -15%
                        </span>
                      </div>
                    ) : (
                      <i
                        className={`bi ${option.icon}`}
                        style={{ fontSize: "2.5rem", color: "#543ba4" }}
                      ></i>
                    )}
                  </div>
                  <h4 className="mb-3">{option.title}</h4>
                  <p className="text-muted mb-4">{option.description}</p>
                  
                  <div className="flex-grow-1">
                    <ul className="list-unstyled text-start mb-4">
                      {option.features.map((feature, index) => (
                        <li key={index} className="mb-2 d-flex align-items-start">
                          <i
                            className="bi bi-check-circle-fill me-2 mt-1 flex-shrink-0"
                            style={{ color: "#543ba4" }}
                          ></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-auto">
                    <Link
                      to="/category"
                      className="btn w-100"
                      style={option.buttonStyle}
                    >
                      {option.buttonText}
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Avantages / Caractéristiques */}
      <section style={styles.grayBg} className="py-5">
        <div className="container py-4">
          <h2 style={styles.sectionTitle}>
            Pourquoi choisir BoxPlaisir ?
            <span style={styles.sectionTitleLine}></span>
          </h2>

          <div className="row g-4">
            {features.map((feature, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <div className="text-center h-100 d-flex flex-column">
                  <div style={styles.featureIcon} className="mx-auto">
                    <i className={`bi ${feature.icon}`}></i>
                  </div>
                  <h5 className="mb-3">{feature.title}</h5>
                  <p className="text-muted small flex-grow-1">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Témoignages / Avis */}
      <section className="py-5">
        <div className="container py-4">
          <h2 style={styles.sectionTitle}>
            Ce que nos clients en disent
            <span style={styles.sectionTitleLine}></span>
          </h2>

          <div className="row g-4 align-items-stretch">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="col-md-4 d-flex">
                <div 
                  className="w-100"
                  style={styles.testimonialCard}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-5px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <div style={styles.testimonialHeader}>
                    <div className="d-flex align-items-center">
                      <div style={styles.testimonialAvatar}>{testimonial.avatar}</div>
                      <div className="ms-3">
                        <h6 className="mb-0 fw-bold">{testimonial.name}</h6>
                        <p className="mb-0 small opacity-75">
                          {testimonial.duration}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="card-body d-flex flex-column">
                    {renderStars(testimonial.rating)}
                    <p className="card-text flex-grow-1">{testimonial.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section
        className="py-5 text-center"
        style={{ backgroundColor: "#543ba4", color: "white" }}
      >
        <div className="container py-4">
          <h2 className="mb-4">Prêt à vous faire plaisir ?</h2>
          <p className="lead mb-4 mx-auto" style={{ maxWidth: "700px" }}>
            Profitez de 10% de réduction sur votre première commande avec le
            code <strong style={{ color: "#f89638" }}>BIENVENUE</strong>
          </p>
          <Link
            to="/category"
            className="btn btn-lg px-5 py-3"
            style={{
              backgroundColor: "#f89638",
              borderColor: "#f89638",
              fontWeight: "600",
              textDecoration: "none",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "#e67e22";
              e.target.style.transform = "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "#f89638";
              e.target.style.transform = "translateY(0)";
            }}
          >
            Découvrir nos box <i className="bi bi-arrow-right ms-2"></i>
          </Link>
        </div>
      </section>
    </>
  );
};

export default Home;